import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { BusinessStatisticItem } from './parts/BusinessStatisticItem/BusinessStatisticItem';
import { BusinessStatisticMainItem } from './parts/BusinessStatisticMainItem/BusinessStatisticMainItem';
import { BusinessStatisticSection } from './parts/BusinessStatisticSection/BusinessStatisticSection';
import { BusinessStatisticSummaryProps } from './types';

export const BusinessStatisticSummary = (props: BusinessStatisticSummaryProps) => {
    const t = useTranslation();
    const { statistic, ...other } = props;
    const { coverage, involved, showsInGrid, shows, subscribers, interactions } = statistic;

    const enableNewStat = useExperimentFlag('enable_new_statistic');

    const subscribersSection = subscribers ? (
        <BusinessStatisticSection
            title={t('Подписчики')}
            helpMessage={
                enableNewStat ? (
                    <Typography variant="body" display="inline">
                        {t(
                            'Количество аккаунтов, подписавшихся на профиль за весь срок существования профиля с изменением в количестве подписавшихся за выбранный период.',
                        )}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="caption" weight="bold" color="var(--color-invert)">
                            {t('Подписчики')}
                            <Typography
                                variant="caption"
                                color="var(--color-invert)"
                                display="inline"
                            >
                                {t(' - количество аккаунтов, подписавшихся на профиль за ')}
                            </Typography>
                            <Typography
                                variant="caption"
                                weight="bold"
                                color="var(--color-invert)"
                                display="inline"
                            >
                                {t('весь срок существования профиля')}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="var(--color-invert)"
                                display="inline"
                            >
                                {t(' с изменением в количестве подписавшихся за ')}
                            </Typography>
                            <Typography
                                variant="caption"
                                weight="bold"
                                color="var(--color-invert)"
                                display="inline"
                            >
                                {t('выбранный период')}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="var(--color-invert)"
                                display="inline"
                            >
                                {t('.')}
                            </Typography>
                        </Typography>
                        <Typography variant="caption" color="var(--color-invert)">
                            {t(
                                'Подписались - количество аккаунтов, подписавшихся за выбранный период.',
                            )}
                        </Typography>
                        <Typography variant="caption" color="var(--color-invert)">
                            {t(
                                'Отписались - количество аккаунтов, отписавшихся от профиля за выбранный период.',
                            )}
                        </Typography>
                    </>
                )
            }
            statsSlot={
                <>
                    <BusinessStatisticMainItem
                        text={t('Количество аккаунтов, подписавшихся на профиль')}
                        displayValue={subscribers.total.display}
                        diffValue={subscribers.total.diffValue}
                        diffDisplay={subscribers.total.diffDisplay}
                    />
                    {enableNewStat ? null : (
                        <>
                            <BusinessStatisticItem
                                title={t('Подписались')}
                                displayValue={subscribers.subscribed.display}
                                diffValue={subscribers.subscribed.diffValue}
                                diffDisplay={subscribers.subscribed.diffDisplay}
                            />
                            <BusinessStatisticItem
                                title={t('Отписались')}
                                displayValue={subscribers.unsubscribed.display}
                                diffValue={subscribers.unsubscribed.diffValue}
                                diffDisplay={subscribers.unsubscribed.diffDisplay}
                            />
                        </>
                    )}
                </>
            }
            borderRight={enableNewStat ? 'none' : { xs: 'none', sm: '1px solid var(--divider)' }}
        />
    ) : null;

    const interactionsSection = interactions ? (
        <BusinessStatisticSection
            title={t('Взаимодействия')}
            helpMessage={
                enableNewStat ? (
                    <Typography variant="body" display="inline">
                        {t(
                            'Cколько раз пользователи взаимодействовали с вашими постами: переходили по ссылкам, ставили реакции, делились и сохраняли публикации. Метрика считается отдельно для органических и продвигаемых публикаций.',
                        )}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="caption" weight="bold" color="var(--color-invert)">
                            {t('Взаимодействия')}
                            <Typography
                                variant="caption"
                                color="var(--color-invert)"
                                display="inline"
                            >
                                {t(
                                    ' - количество действий, которые совершали пользователи с контентом за выбранный период с изменением относительно предыдущего периода.',
                                )}
                            </Typography>
                        </Typography>
                        <Typography variant="caption" color="var(--color-invert)">
                            {t(
                                'Клики по ссылкам - количество переходов по ссылкам из поста и профиля.',
                            )}
                        </Typography>
                        <Typography variant="caption" color="var(--color-invert)">
                            {t('Лайки - количество лайков у контента.')}
                        </Typography>
                        <Typography variant="caption" color="var(--color-invert)">
                            {t('Шаринги - количество шарингов контента.')}
                        </Typography>
                        <Typography variant="caption" color="var(--color-invert)">
                            {t('Сохранения - количество сохранненого контента в избранное.')}
                        </Typography>
                    </>
                )
            }
            statsSlot={
                <>
                    <BusinessStatisticMainItem
                        text={
                            enableNewStat
                                ? t(
                                      'Сколько раз пользователи взаимодействовали с вашими постами: ставили реакции, делились публикациями, сохраняли их, переходили по ссылкам',
                                  )
                                : t(
                                      'Количество действий, которые совершили пользователи с контентом',
                                  )
                        }
                        displayValue={interactions.total.display}
                        diffValue={interactions.total.diffValue}
                        diffDisplay={interactions.total.diffDisplay}
                    />
                    {enableNewStat ? (
                        <Flex direction="row" width="100%" spacing={2}>
                            <Flex width="100%">
                                <BusinessStatisticItem
                                    direction="column"
                                    title={t('Клики по ссылке')}
                                    displayValue={interactions.clicks.display}
                                    diffValue={interactions.clicks.diffValue}
                                    diffDisplay={interactions.clicks.diffDisplay}
                                    isFirstValue
                                />
                                <BusinessStatisticItem
                                    direction="column"
                                    title={t('Лайки')}
                                    displayValue={interactions.likes.display}
                                    diffValue={interactions.likes.diffValue}
                                    diffDisplay={interactions.likes.diffDisplay}
                                    isFirstValue
                                />
                            </Flex>
                            <Flex width="100%">
                                <BusinessStatisticItem
                                    direction="column"
                                    title={t('Шаринги')}
                                    displayValue={interactions.shares.display}
                                    diffValue={interactions.shares.diffValue}
                                    diffDisplay={interactions.shares.diffDisplay}
                                    isFirstValue
                                />
                                <BusinessStatisticItem
                                    direction="column"
                                    title={t('Сохранения')}
                                    displayValue={interactions.saves.display}
                                    diffValue={interactions.saves.diffValue}
                                    diffDisplay={interactions.saves.diffDisplay}
                                    isFirstValue
                                />
                            </Flex>
                        </Flex>
                    ) : (
                        <>
                            <BusinessStatisticItem
                                title={t('Клики по ссылке')}
                                displayValue={interactions.clicks.display}
                                diffValue={interactions.clicks.diffValue}
                                diffDisplay={interactions.clicks.diffDisplay}
                            />
                            <BusinessStatisticItem
                                title={t('Лайки')}
                                displayValue={interactions.likes.display}
                                diffValue={interactions.likes.diffValue}
                                diffDisplay={interactions.likes.diffDisplay}
                            />
                            <BusinessStatisticItem
                                title={t('Шаринги')}
                                displayValue={interactions.shares.display}
                                diffValue={interactions.shares.diffValue}
                                diffDisplay={interactions.shares.diffDisplay}
                            />
                            <BusinessStatisticItem
                                title={t('Сохранения')}
                                displayValue={interactions.saves.display}
                                diffValue={interactions.saves.diffValue}
                                diffDisplay={interactions.saves.diffDisplay}
                            />
                        </>
                    )}
                </>
            }
        />
    ) : null;

    return (
        <Flex width="100%" flexWrap="wrap" direction="row" alignItems="stretch" {...other}>
            {coverage ? (
                <BusinessStatisticSection
                    title={t('Охват')}
                    helpMessage={
                        enableNewStat ? (
                            <Typography variant="body">
                                {t(
                                    'Количество уникальных пользователей, которые увидели как органические, так и продвигаемые посты.',
                                )}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                >
                                    {t('Охват')}
                                    <Typography
                                        variant="caption"
                                        color="var(--color-invert)"
                                        display="inline"
                                    >
                                        {t(
                                            ' - количество уникальных аккаунтов, владельцы которых хотя бы 1 раз видели ваш контент за выбранный период с изменением в процентах относительного предыдущего периода.',
                                        )}
                                    </Typography>
                                </Typography>
                                <Typography variant="caption" color="var(--color-invert)">
                                    {t(
                                        'Эта метрика находится в разработке и возможны изменения в расчетах.',
                                    )}
                                </Typography>
                                <Typography variant="caption" color="var(--color-invert)">
                                    {t(
                                        'Охват считается отдельно для пользователей- подписчиков и пользователей, которые не подписаны на профиль.',
                                    )}
                                </Typography>
                            </>
                        )
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={
                                    enableNewStat
                                        ? t(
                                              'Сколько уникальных пользователей хотя бы один раз увидели ваши посты',
                                          )
                                        : t('Количество уникальных аккаунтов')
                                }
                                displayValue={coverage.total.display}
                                diffValue={coverage.total.diffValue}
                                diffDisplay={coverage.total.diffDisplay}
                            />
                            {enableNewStat ? (
                                <>
                                    {coverage.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={coverage.organic.display}
                                            diffValue={coverage.organic.diffValue}
                                            diffDisplay={coverage.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {coverage.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={coverage.boosted.display}
                                            diffValue={coverage.boosted.diffValue}
                                            diffDisplay={coverage.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <BusinessStatisticItem
                                        title={t('Подписчики')}
                                        displayValue={coverage.subscribers.display}
                                        diffValue={coverage.subscribers.diffValue}
                                        diffDisplay={coverage.subscribers.diffDisplay}
                                    />
                                    <BusinessStatisticItem
                                        title={t('Не подписчики')}
                                        displayValue={coverage.notsubscribers.display}
                                        diffValue={coverage.notsubscribers.diffValue}
                                        diffDisplay={coverage.notsubscribers.diffDisplay}
                                    />
                                </>
                            )}
                        </>
                    }
                    borderTop="none"
                    borderRight={{ xs: 'none', sm: '1px solid var(--divider)' }}
                />
            ) : null}

            {involved ? (
                <BusinessStatisticSection
                    title={t('Вовлеченные аккаунты')}
                    helpMessage={
                        enableNewStat ? (
                            <Typography variant="body">
                                {t(
                                    'Количество уникальных пользователей, которые взаимодействовали с вашими постами. Метрика считается отдельно для органических и продвигаемых публикаций.',
                                )}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                >
                                    {t('Вовлеченные аккаунты')}
                                    <Typography
                                        variant="caption"
                                        color="var(--color-invert)"
                                        display="inline"
                                    >
                                        {t(
                                            ' - количество уникальных аккаунтов, владельцы которых совершили какое-либо полезное действие с контентом профиля за выбранный период с изменением в процентах относительного предыдущего периода. Полезные действия - подписка, лайк, сохранение в избранное, поделиться контентом.',
                                        )}
                                    </Typography>
                                </Typography>
                                <Typography variant="caption" color="var(--color-invert)">
                                    {t(
                                        'Эта метрика находится в разработке и возможны изменения в расчетах.',
                                    )}
                                </Typography>
                                <Typography variant="caption" color="var(--color-invert)">
                                    {t(
                                        'Вовлеченные аккаунты считаются отдельно для пользователей - подписчиков и пользователей, которые не подписаны на профиль.',
                                    )}
                                </Typography>
                            </>
                        )
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={
                                    enableNewStat
                                        ? t(
                                              'Сколько уникальных пользователей взаимодействовали с вашими постами',
                                          )
                                        : t(
                                              'Количество уникальных аккаунтов, владельцы которых совершили какое-либо полезное действие',
                                          )
                                }
                                displayValue={involved.total.display}
                                diffValue={involved.total.diffValue}
                                diffDisplay={involved.total.diffDisplay}
                            />
                            {enableNewStat ? (
                                <>
                                    {involved.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={involved.organic.display}
                                            diffValue={involved.organic.diffValue}
                                            diffDisplay={involved.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {involved.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={involved.boosted.display}
                                            diffValue={involved.boosted.diffValue}
                                            diffDisplay={involved.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <BusinessStatisticItem
                                        title={t('Подписчики')}
                                        displayValue={involved.subscribers.display}
                                        diffValue={involved.subscribers.diffValue}
                                        diffDisplay={involved.subscribers.diffDisplay}
                                    />
                                    <BusinessStatisticItem
                                        title={t('Не подписчики')}
                                        displayValue={involved.notsubscribers.display}
                                        diffValue={involved.notsubscribers.diffValue}
                                        diffDisplay={involved.notsubscribers.diffDisplay}
                                    />
                                </>
                            )}
                        </>
                    }
                    borderTop={{ xs: '1px solid var(--divider)', sm: 'none' }}
                />
            ) : null}

            {showsInGrid ? (
                <BusinessStatisticSection
                    title={t('Показы в ленте')}
                    helpMessage={
                        enableNewStat ? (
                            <Typography variant="body" display="inline">
                                {t(
                                    'Сколько раз пользователи увидели ваши посты. Метрика считается отдельно для органических и продвигаемых публикаций.',
                                )}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                >
                                    {t('Показы в ленте')}
                                    <Typography
                                        variant="caption"
                                        color="var(--color-invert)"
                                        display="inline"
                                    >
                                        {t(' - все показы ваших постов в ленте.')}
                                    </Typography>
                                </Typography>
                            </>
                        )
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={
                                    enableNewStat
                                        ? t(
                                              'Сколько раз пользователи увидели ваши посты в ленте Ритма',
                                          )
                                        : t('Все показы ваших постов в ленте')
                                }
                                displayValue={showsInGrid.total.display}
                                diffValue={showsInGrid.total.diffValue}
                                diffDisplay={showsInGrid.total.diffDisplay}
                            />
                            {enableNewStat ? (
                                <>
                                    {showsInGrid.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={showsInGrid.organic.display}
                                            diffValue={showsInGrid.organic.diffValue}
                                            diffDisplay={showsInGrid.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {showsInGrid.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={showsInGrid.boosted.display}
                                            diffValue={showsInGrid.boosted.diffValue}
                                            diffDisplay={showsInGrid.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <BusinessStatisticItem
                                        title={t('Подписчики')}
                                        displayValue={showsInGrid.subscribers.display}
                                        diffValue={showsInGrid.subscribers.diffValue}
                                        diffDisplay={showsInGrid.subscribers.diffDisplay}
                                    />
                                    <BusinessStatisticItem
                                        title={t('Не подписчики')}
                                        displayValue={showsInGrid.notsubscribers.display}
                                        diffValue={showsInGrid.notsubscribers.diffValue}
                                        diffDisplay={showsInGrid.notsubscribers.diffDisplay}
                                    />
                                </>
                            )}
                        </>
                    }
                    borderRight={{ xs: 'none', sm: '1px solid var(--divider)' }}
                />
            ) : null}

            {shows ? (
                <BusinessStatisticSection
                    title={t('Показы с действием')}
                    helpMessage={
                        enableNewStat ? (
                            <Typography variant="body" display="inline">
                                {t(
                                    'Количество показов, после которых пользователи взаимодействовали с постом: открыли пост на ya.ru, в веб-версии или приложении, навели курсор на пост, поставили реакцию или сохранили публикацию. Метрика считается отдельно для органических и продвигаемых публикаций.',
                                )}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                >
                                    {t('Показы с действием')}
                                    <Typography
                                        variant="caption"
                                        color="var(--color-invert)"
                                        display="inline"
                                    >
                                        {t(
                                            ' - показы с активным взаимодействием пользователя с постом, таких как открытие полноэкранного формата поста на ya.ru, открытие поста в вебе или приложении, наведение на пост, лайки, сохранения и тд.',
                                        )}
                                    </Typography>
                                </Typography>
                            </>
                        )
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={
                                    enableNewStat
                                        ? t(
                                              'Количество показов, после которых пользователи взаимодействовали с постом',
                                          )
                                        : t(
                                              'Показы с активным взаимодействием пользователя с постом',
                                          )
                                }
                                displayValue={shows.total.display}
                                diffValue={shows.total.diffValue}
                                diffDisplay={shows.total.diffDisplay}
                            />
                            {enableNewStat ? (
                                <>
                                    {shows.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={shows.organic.display}
                                            diffValue={shows.organic.diffValue}
                                            diffDisplay={shows.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {shows.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={shows.boosted.display}
                                            diffValue={shows.boosted.diffValue}
                                            diffDisplay={shows.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <BusinessStatisticItem
                                        title={t('Подписчики')}
                                        displayValue={shows.subscribers.display}
                                        diffValue={shows.subscribers.diffValue}
                                        diffDisplay={shows.subscribers.diffDisplay}
                                    />
                                    <BusinessStatisticItem
                                        title={t('Не подписчики')}
                                        displayValue={shows.notsubscribers.display}
                                        diffValue={shows.notsubscribers.diffValue}
                                        diffDisplay={shows.notsubscribers.diffDisplay}
                                    />
                                </>
                            )}
                        </>
                    }
                />
            ) : null}
            {enableNewStat
                ? [interactionsSection, subscribersSection]
                : [subscribersSection, interactionsSection]}
        </Flex>
    );
};
